import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetGphContractQuery } from "../api";
import { calculateContractSummary, calculateServiceItemSummaries } from "../utils";
import type { CalculationsSummary, GphCompletionAct, ServiceItemSummary } from "../types";

export function useCalculationsPage() {
  const { t, i18n } = useTranslation("EmployeePortal");
  const locale = (i18n.language as "ru" | "kk" | "en") || "ru";
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const { data: contract, isLoading, isError, error } = useGetGphContractQuery();

  const summary: CalculationsSummary = useMemo(() => {
    if (!contract) {
      return {
        contractAmount: 0,
        totalPaid: 0,
        totalApproved: 0,
        totalPending: 0,
        remaining: 0,
        paidPercent: 0,
        approvedPercent: 0,
      };
    }
    return calculateContractSummary(contract.contract_amount, contract.completion_acts);
  }, [contract]);

  const serviceItemSummaries: ServiceItemSummary[] = useMemo(() => {
    if (!contract) return [];
    return calculateServiceItemSummaries(contract.service_items, contract.completion_acts, contract.shared_price);
  }, [contract]);

  const sortedActs: GphCompletionAct[] = useMemo(() => {
    if (!contract) return [];
    return [...contract.completion_acts].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [contract]);

  const isNotFound = isError && error && "status" in error && (error.status === 404 || error.status === 403);

  return {
    contract,
    isLoading,
    isError,
    isNotFound,
    summary,
    serviceItemSummaries,
    sortedActs,
    createModalOpen,
    setCreateModalOpen,
    t,
    locale,
  };
}
